<script setup lang="ts">
import { onBeforeUnmount, onMounted, useTemplateRef } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryTile> in <template>
import ClassicPortraitGalleryTile from './ClassicPortraitGalleryTile.vue';
import type {
  ClassicPortraitGalleryOrientation,
  MixedPortraitGalleryItem,
} from './classic-mixed-portrait-gallery';

interface Props {
  getSelectedPortraitCount: (
    item: MixedPortraitGalleryItem
  ) => number | undefined;
  items: MixedPortraitGalleryItem[];
  orientation: ClassicPortraitGalleryOrientation;
  selectedUuids: string[];
}

const props = defineProps<Props>();
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  activate: [item: MixedPortraitGalleryItem, index: number, event: MouseEvent];
  countDecrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  countIncrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  itemLoad: [item: MixedPortraitGalleryItem, index: number, event: Event];
  // The height that a tile can take in the block. In landscape the gallery
  // makes the block as wide as a tile of that height needs.
  tileHeight: [height: number];
  tryAgain: [item: MixedPortraitGalleryItem];
}>();

const itemsElement = useTemplateRef<HTMLElement>('items');
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  const element = itemsElement.value;
  if (!element || typeof ResizeObserver === 'undefined') {
    return;
  }
  resizeObserver = new ResizeObserver(([entry]) => {
    if (entry) {
      emit('tileHeight', entry.contentRect.height);
    }
  });
  resizeObserver.observe(element);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function isSelected(item: MixedPortraitGalleryItem): boolean {
  return props.selectedUuids.includes(item.uuid);
}
</script>

<template>
  <section
    class="ClassicPortraitGalleryGroupBlock"
    :class="`ClassicPortraitGalleryGroupBlock--${orientation}`"
    data-testid="portraits-group-root"
    :aria-label="$t('Portraits.stageKindGroup')"
  >
    <div class="ClassicPortraitGalleryGroupBlock__caption" aria-hidden="true">
      {{ $t('Portraits.stageKindGroup') }}
    </div>
    <div ref="items" class="ClassicPortraitGalleryGroupBlock__items">
      <ClassicPortraitGalleryTile
        v-for="(item, index) in items"
        :key="item.renderKey ?? item.uuid"
        class="ClassicPortraitGalleryGroupBlock__tile"
        :item="item"
        :index="index"
        :active="false"
        :selected="isSelected(item)"
        :count="getSelectedPortraitCount(item)"
        selection-on-tile
        @activate="emit('activate', item, index, $event)"
        @item-load="emit('itemLoad', item, index, $event)"
        @try-again="emit('tryAgain', item)"
        @count-decrement="emit('countDecrement', $event, item)"
        @count-increment="emit('countIncrement', $event, item)"
      >
        <template #status="{ pending }">
          <p
            v-if="pending"
            class="ClassicPortraitGalleryGroupBlock__status"
            data-testid="portraits-group-placeholder"
          >
            {{ $t('Portraits.groupPlaceholder') }}
          </p>
          <p
            v-else-if="item.isError"
            class="ClassicPortraitGalleryGroupBlock__status"
            data-testid="portraits-group-error"
          >
            {{ $t('Portraits.error') }}
          </p>
        </template>
      </ClassicPortraitGalleryTile>
    </div>
  </section>
</template>

<style scoped lang="scss">
/* The block holds the group portraits apart from the singles: a frame and a
   caption over its own scroll area. The gallery sets the block size. Each
   tile fits the area of the block with the ratio of its image, thus a
   second group portrait (from "+") goes below the first in landscape and
   beside it in portrait orientation. */
.ClassicPortraitGalleryGroupBlock {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 0.6rem;
  min-width: 0;
  min-height: 0;
  padding: 0.8rem;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.06);
  border: 1px solid rgba(var(--Kiosk--tint-color-rgb), 0.18);
  border-radius: calc(var(--cmg-radius, 0.8rem) * 1.5);
}

.ClassicPortraitGalleryGroupBlock__caption {
  font-size: 1.15rem;
  color: var(--Kiosk--font_color);
  text-align: center;
  text-transform: var(--Kiosk--font_capitalize, uppercase);
}

.ClassicPortraitGalleryGroupBlock__items {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  gap: var(--cmg-gap, 0.8rem);
  min-width: 0;
  min-height: 0;
  overflow: auto;
  container-type: size;
  scrollbar-width: none;
}

.ClassicPortraitGalleryGroupBlock--portrait
  .ClassicPortraitGalleryGroupBlock__items {
  flex-direction: row;
}

.ClassicPortraitGalleryGroupBlock__tile {
  --cmg-tile-max-height: 100cqh;
  --cmg-tile-max-width: 100cqw;
}

.ClassicPortraitGalleryGroupBlock__status {
  position: absolute;
  top: calc(50% + 1.6rem);
  right: 0;
  left: 0;
  z-index: 2;
  margin: 0;
  padding: 0 8%;
  font-size: 1.1rem;
  line-height: 1.3;
  color: #ffffff;
  text-align: center;
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  pointer-events: none;
}
</style>
